/*
 * Copyright (c) 2022, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React from 'react'
import PropTypes from 'prop-types'
import {Box, Text, Flex, useMultiStyleConfig} from '@salesforce/retail-react-app/app/components/shared/ui'
import {FormattedMessage} from 'react-intl'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {useBrand} from '../../../contexts/brand-context'
import {getSiteEnvVarValue} from '../../../utils/site-config'

/**
 * Displays "Showing X of Y items" above the load-more CTA on the PLP.
 * Controlled per brand via ENABLE_PLP_ITEMS_COUNTER_DYNAMITE /
 * ENABLE_PLP_ITEMS_COUNTER_GARAGE env vars.
 */
const ItemsCounter = ({loadedCount, totalCount}) => {
    const styles = useMultiStyleConfig('ItemsCounter')
    const {isDynamite} = useBrand()
    const {site} = useMultiSite()
    const {app} = getConfig()

    const flagKey = isDynamite
        ? 'ENABLE_PLP_ITEMS_COUNTER_DYNAMITE'
        : 'ENABLE_PLP_ITEMS_COUNTER_GARAGE'

    if (getSiteEnvVarValue(app, flagKey, site?.id) !== 'true' || !totalCount) {
        return null
    }

    const percentage = totalCount > 0 ? (loadedCount / totalCount) * 100 : 0

    return (
        <Flex {...styles.container} data-testid="plp-items-counter">
            <Text sx={styles.textProgress}>
                <FormattedMessage
                    id="plp.items_counter.showing"
                    defaultMessage="Showing {loaded} of {total} items"
                    values={{
                        loaded: (
                            <Text as="span" sx={styles.value}>
                                {loadedCount}
                            </Text>
                        ),
                        total: (
                            <Text as="span" sx={styles.value}>
                                {totalCount}
                            </Text>
                        )
                    }}
                />
            </Text>
            <Box {...styles.progress}>
                <Box {...styles.progressBar} width={`${percentage}%`}/>
            </Box>
        </Flex>
    )
}

ItemsCounter.propTypes = {
    loadedCount: PropTypes.number.isRequired,
    totalCount: PropTypes.number.isRequired
}

export default ItemsCounter
